'use client'

import { useState, useEffect } from 'react'
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'
import { useProjectStore } from '@/hooks/use-project-store'
import Image from 'next/image'
import { generateShortId } from '@/lib/utils'
import { useCurrentUser } from '@/hooks/use-current-user'
import { usePlayConfetti } from '@/contexts/confetti'
import { CheckCircle2, Sparkles, Copy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FireworkCanvas } from '@/components/firework-canvas'
import { useRef } from 'react'

export const PublishResultModal = ({ buildUrl }: { buildUrl: string }) => {
  const t = useTranslations('publish-result')
  const tModal = useTranslations('publish-modal')
  const { project, setProject } = useProjectStore()
  const { data: user } = useCurrentUser()
  const { show: playConfetti } = usePlayConfetti()
  const [domainStatus, setDomainStatus] = useState<Record<string, boolean>>()
  const [showCelebration, setShowCelebration] = useState(true)
  const fireworkRef = useRef<{ canvas: HTMLCanvasElement | null }>(null)

  // Play confetti and fireworks on mount
  useEffect(() => {
    playConfetti(5000)
    // Hide celebration animation after 2 seconds
    setTimeout(() => setShowCelebration(false), 2000)
  }, [playConfetti])

  // Fetch domain status for custom domains with polling (no auto-publish in result modal)
  useEffect(() => {
    const domains = [project?.own_domain, project?.new_domain].filter(Boolean)
    if (domains.length === 0) {
      setDomainStatus({})
      return
    }

    let pollInterval: NodeJS.Timeout | null = null

    const loadDomainStatus = async () => {
      try {
        const result = await fetch('/api/entri/status', {
          method: 'POST',
          body: JSON.stringify({ domains }),
        })
        if (result.ok) {
          const data = await result.json()
          setDomainStatus(data)
        } else {
          setDomainStatus({})
        }
      } catch (error) {
        console.error('Error loading domain status:', error)
        setDomainStatus({})
      }
    }

    // Initial load
    loadDomainStatus()

    // Poll every 5 seconds to update status display
    pollInterval = setInterval(() => {
      loadDomainStatus()
    }, 5000)

    // Cleanup on unmount
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
  }, [project?.own_domain, project?.new_domain])

  const getDomainStatus = (domain: string) => {
    if (!domainStatus) {
      return (
        <div className="text-[10px] font-semibold text-white/50 bg-white/5 py-0.5 px-2 rounded-full whitespace-nowrap border border-white/10">
          {tModal('status.loading')}
        </div>
      )
    }

    const verified = Boolean(domainStatus[domain])
    if (verified) {
      return (
        <div className="text-[10px] font-semibold text-emerald-300 bg-emerald-900/70 py-0.5 px-2 rounded-full whitespace-nowrap border border-emerald-400/70 shadow-[0_0_12px_rgba(16,185,129,0.7)]">
          {tModal('status.connected')}
        </div>
      )
    } else {
      return (
        <div className="text-[10px] font-semibold text-gold/70 bg-gold/10 py-0.5 px-2 rounded-full whitespace-nowrap border border-gold/20">
          {tModal('status.pending')}
        </div>
      )
    }
  }

  // Generate the default heyboss domain
  const subdomain =
    user?.username ||
    (user?._id ? generateShortId(user._id.toString()) : 'username')
  const projectId = project?._id?.toString() || ''
  const defaultUrl =
    project?.template_type === 'worker'
      ? `https://${project._id}.heyboss.live`
      : `https://${subdomain}.heyboss.tech/${generateShortId(projectId)}/`

  // Collect all domains in the correct order: new_domain -> own_domain -> default
  const allDomains: Array<{
    url: string
    type: string
    domain: string | null
  }> = []

  if (project?.new_domain) {
    allDomains.push({
      url: `https://${project.new_domain}`,
      type: 'new',
      domain: project.new_domain,
    })
  }

  if (project?.own_domain) {
    allDomains.push({
      url: `https://${project.own_domain}`,
      type: 'custom',
      domain: project.own_domain,
    })
  }

  allDomains.push({ url: defaultUrl, type: 'default', domain: null })

  // Selected domain state - default to project's default_domain or first domain
  const [selectedDomain, setSelectedDomain] = useState<string>(
    project?.default_domain || allDomains[0]?.url || defaultUrl,
  )

  // Update selected domain when project changes
  useEffect(() => {
    if (project?.default_domain) {
      setSelectedDomain(project.default_domain)
    }
  }, [project?.default_domain])

  // Check if any domain has pending status
  const hasPendingDomain = allDomains.some(
    (d) => d.domain && domainStatus && !Boolean(domainStatus[d.domain]),
  )

  const copyToClipboard = (url: string) => {
    navigator.clipboard.writeText(url || '')
    toast.success('Copied to clipboard')
  }

  const handleDomainSelect = async (url: string) => {
    setSelectedDomain(url)

    // Update project with selected default domain
    if (project?._id) {
      try {
        await fetch(`/api/projects/${project._id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ default_domain: url }),
        })

        // Update local project store
        setProject({ ...project, default_domain: url })
      } catch (error) {
        console.error('Failed to update default domain:', error)
      }
    }
  }

  const hasMultipleDomains = allDomains.length > 1

  return (
    <>
      {/* Firework Canvas for celebration */}
      <FireworkCanvas
        ref={fireworkRef}
        settings={{
          theme: 'neonTokyo',
          autoLaunch: true,
          maxFireworks: 8,
          particleDensity: 150,
          transparent: true,
        }}
        autoLaunch={true}
      />
      <DialogContent
        className="max-w-[calc(100vw-24px)] sm:max-w-[440px] rounded-2xl border border-gold/30 bg-gradient-to-br from-[#1b1405] via-[#06030a] to-[#020208] shadow-[0_22px_80px_rgba(0,0,0,0.85)] backdrop-blur-2xl px-0 pt-0 pb-4 overflow-hidden"
        overlayClassName="bg-black/60 backdrop-blur-sm"
      >
        <DialogHeader className="px-5 pt-4 pb-2 relative">
          {/* Celebration Animation */}
          {showCelebration && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
              <div className="relative">
                <div className="absolute inset-0 flex items-center justify-center">
                  <CheckCircle2 className="w-16 h-16 text-gold animate-scale-in drop-shadow-[0_0_20px_rgba(255,215,0,0.5)]" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  {[...Array(8)].map((_, i) => {
                    const angle = (i * 360) / 8
                    const radius = 32
                    const x = Math.cos((angle * Math.PI) / 180) * radius
                    const y = Math.sin((angle * Math.PI) / 180) * radius
                    return (
                      <Sparkles
                        key={i}
                        className="absolute w-4 h-4 text-gold"
                        style={{
                          left: `calc(50% + ${x}px)`,
                          top: `calc(50% + ${y}px)`,
                          transform: 'translate(-50%, -50%)',
                          animation: `fade-in-scale 0.6s ease-out ${i * 0.1}s forwards`,
                          opacity: 0,
                        }}
                      />
                    )
                  })}
                </div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 mb-1">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-gold/60 via-gold/40 to-amber-400/40 flex items-center justify-center border border-gold/60 shadow-[0_0_18px_rgba(250,204,21,0.8)]">
              <CheckCircle2 className="w-4 h-4 text-black" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-left text-base sm:text-lg font-semibold text-white mb-0 leading-tight tracking-tight">
                {t('title')}
              </DialogTitle>
              <p className="text-[11px] sm:text-xs text-white/70 leading-relaxed">
                Your project is now live — share this golden moment with the
                world. 🎉
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 px-5 pt-1 pb-1">
          {/* Warning message if any domain is pending */}
          {hasPendingDomain && (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-black/30 border border-amber-300/40 rounded-xl shadow-[0_0_24px_rgba(252,211,77,0.25)]">
              <Image
                src="/ai-chat/triangle-warn-2.svg"
                alt="Warning"
                width={14}
                height={14}
                className="flex-shrink-0 mt-0.5"
              />
              <div className="text-amber-100 text-xs flex-1">
                {tModal('domain_pending')}
              </div>
            </div>
          )}

          {/* Domain Selection - Only show if multiple domains */}
          {hasMultipleDomains && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-white/80">
                Pick a default domain
              </p>
              <RadioGroup
                value={selectedDomain}
                onValueChange={handleDomainSelect}
              >
                <div className="space-y-2">
                  {allDomains.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 w-full min-w-0 p-2.5 rounded-xl bg-black/40 border border-gold/25 hover:border-gold/40 transition-colors shadow-[0_0_20px_rgba(15,23,42,0.7)]"
                    >
                      <RadioGroupItem
                        value={item.url}
                        id={`domain-${index}`}
                        className="border-gold/50 text-gold flex-shrink-0 w-4 h-4 mt-0.5 [&[data-state=checked]]:border-gold [&[data-state=checked]]:text-gold"
                      />
                      <label
                        htmlFor={`domain-${index}`}
                        className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer overflow-hidden"
                      >
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs font-medium text-gold hover:text-gold/80 transition-colors underline underline-offset-2 flex-1 min-w-0 break-words"
                          style={{
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {item.url}
                        </a>
                        {item.type === 'default' ? (
                          <div className="text-[10px] font-semibold text-emerald-300 bg-emerald-900/70 py-0.5 px-2 rounded-full whitespace-nowrap flex-shrink-0 border border-emerald-400/70 shadow-[0_0_12px_rgba(16,185,129,0.7)]">
                            {tModal('status.connected')}
                          </div>
                        ) : (
                          item.domain && getDomainStatus(item.domain)
                        )}
                      </label>
                      <button
                        className="p-1.5 rounded-full transition-colors flex-shrink-0 hover:bg-gold/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/70"
                        onClick={(e) => {
                          e.stopPropagation()
                          copyToClipboard(item.url)
                        }}
                        type="button"
                        aria-label="Copy URL"
                      >
                        <Copy className="w-3.5 h-3.5 text-gold/90" />
                      </button>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </div>
          )}

          {/* Single Domain Display - If only one domain */}
          {!hasMultipleDomains && allDomains.length > 0 && (
            <div className="flex items-center gap-2 p-2.5 rounded-xl bg-black/40 border border-gold/25 overflow-hidden shadow-[0_0_20px_rgba(15,23,42,0.7)] mx-5 mt-1">
              <a
                href={allDomains[0].url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-0 text-xs font-medium text-gold hover:text-gold/80 transition-colors underline underline-offset-2 break-words"
                style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
              >
                {allDomains[0].url}
              </a>
              <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                {allDomains[0].type === 'default' ? (
                  <div className="text-[10px] font-semibold text-emerald-300 bg-emerald-900/70 py-0.5 px-2 rounded-full whitespace-nowrap border border-emerald-400/70 shadow-[0_0_12px_rgba(16,185,129,0.7)]">
                    {tModal('status.connected')}
                  </div>
                ) : (
                  allDomains[0].domain && getDomainStatus(allDomains[0].domain)
                )}
                <button
                  className="p-1.5 rounded-full hover:bg-gold/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/70"
                  onClick={(e) => {
                    e.preventDefault()
                    copyToClipboard(allDomains[0].url)
                  }}
                  type="button"
                  aria-label="Copy URL"
                >
                  <Copy className="w-3.5 h-3.5 text-gold/90" />
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <style jsx>{`
        @keyframes fade-in-scale {
          from {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }
        @keyframes scale-in {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.5s ease-out;
        }
      `}</style>
    </>
  )
}
